// Analyse de rentabilité fournisseur — moteur local du Copilote (sans LLM).
import { fetchReceptions, fetchTiers, decompte, prixReference, familleLot } from './dataProvider';
import { performanceFournisseurs } from './analytique';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const fmt = (v, d = 0) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(v) || 0);

const MOTS = ['rendement', 'cout de revient', 'coût de revient', 'rentab', 'gagn', 'perd', 'volume d', 'huile', 'performance', 'bilan'];

export function estQuestionFournisseur(q) {
  const t = norm(q);
  return MOTS.some((m) => t.includes(norm(m)));
}

// Le moteur local ne se déclenche que si un fournisseur connu est réellement nommé :
// sinon la question est générale et doit partir au Copilote IA.
export async function detecterFournisseur(question) {
  const t = norm(question);
  return (await fetchTiers())
    .filter((c) => c.estFournisseur !== false)
    .filter((c) => c.nom && norm(c.nom).split(' ').filter((w) => w.length > 2).every((w) => t.includes(w)))
    .sort((a, b) => b.nom.length - a.nom.length)[0] ?? null;
}

export async function analyserFournisseur(question) {
  const t = norm(question);
  const tiers = (await fetchTiers()).filter((c) => c.estFournisseur !== false);
  const cible = tiers
    .filter((c) => c.nom && norm(c.nom).split(' ').filter((w) => w.length > 2).every((w) => t.includes(w)))
    .sort((a, b) => b.nom.length - a.nom.length)[0];
  if (!cible) return null;

  const mois = MOIS.findIndex((m) => t.includes(norm(m)));
  const annee = (t.match(/20\d{2}/) || [])[0];
  const dans = (d) => (mois < 0 || Number(d.slice(5, 7)) === mois + 1) && (!annee || d.startsWith(annee));

  const lots = (await fetchReceptions()).filter((r) => r.tiersId === cible.id && r.parcours === 'fournisseur' && dans(r.date));
  const periode = mois >= 0 ? `sur ${MOIS[mois]}${annee ? ` ${annee}` : ''}` : annee ? `sur ${annee}` : 'toutes périodes';
  if (!lots.length) return `Aucun achat d’olives enregistré pour **${cible.nom}** (${cible.code ?? ''}) ${periode}.`;

  const olives = lots.reduce((s, r) => s + decompte(r).poidsNet, 0);
  const paye = lots.reduce((s, r) => s + (decompte(r).enAttenteRendement ? 0 : decompte(r).montant), 0);
  const tritures = lots.filter((r) => Number(r.huileExtraite) > 0);
  const huile = tritures.reduce((s, r) => s + Number(r.huileExtraite), 0);
  const olivesTriturees = tritures.reduce((s, r) => s + decompte(r).poidsNet, 0);
  const rendement = olivesTriturees > 0 ? (huile / olivesTriturees) * 100 : null;
  const payeTritures = tritures.reduce((s, r) => s + (decompte(r).enAttenteRendement ? 0 : decompte(r).montant), 0);
  const coutKg = huile > 0 ? payeTritures / huile : null;
  const prixRef = prixReference(tritures.some((r) => familleLot(r) === 'lampante') && tritures.every((r) => familleLot(r) === 'lampante') ? 'lampante' : 'noble');
  const valeurHuile = huile * prixRef;
  const marge = valeurHuile - payeTritures;
  const mixes = tritures.filter((r) => r.ofId && lots.some((x) => x !== r && x.ofId === r.ofId));

  const lignes = [
    `**Analyse fournisseur — ${cible.nom}** (${cible.code ?? '—'}) · ${periode}`,
    '',
    `• Olives achetées : **${fmt(olives)} kg** sur ${lots.length} bon(s) · total payé / dû : **${fmt(paye, 3)} TND**`,
    tritures.length
      ? `• Huile réelle issue de ses lots (retour d’atelier) : **${fmt(huile, 1)} kg** pour ${fmt(olivesTriturees)} kg triturés (${tritures.length}/${lots.length} lots) → rendement moyen **${fmt(rendement, 2)} %**`
      : '• Aucun lot encore trituré : rendement et coût de revient indisponibles pour l’instant.',
  ];
  if (coutKg != null) {
    lignes.push(`• Coût de revient : **${fmt(coutKg, 3)} TND / kg d’huile** (${fmt(payeTritures, 3)} TND payés ÷ ${fmt(huile, 1)} kg) — référence marché ${fmt(prixRef, 3)} TND/kg`);
    lignes.push('');
    lignes.push(
      marge >= 0
        ? `✅ **Verdict : ce fournisseur vous a fait GAGNER ${fmt(marge, 3)} TND** (huile valorisée ${fmt(valeurHuile, 3)} TND au prix de référence). Marge de ${fmt(((prixRef - coutKg) / prixRef) * 100, 1)} % sur le prix de marché.`
        : `❌ **Verdict : ce fournisseur vous a fait PERDRE ${fmt(-marge, 3)} TND** — rendement trop faible (${fmt(rendement, 2)} %) par rapport au prix d’achat : coût de revient ${fmt(coutKg, 3)} TND/kg > référence ${fmt(prixRef, 3)} TND/kg.`
    );
    const seuil = olivesTriturees > 0 ? (payeTritures / (olivesTriturees * prixRef)) * 100 : null;
    if (seuil != null) lignes.push(`• Rendement d’équilibre à ce prix d’achat : **${fmt(seuil, 2)} %** (au-dessus = gain, en dessous = perte).`);
  }
  if (mixes.length) lignes.push(`⚠️ ${mixes.length} lot(s) triturés en OF groupé avec d’autres lots : rendement de lot mixé, attribué au prorata du poids d’olives.`);
  // Historique pondéré (§15) : écart fournisseur pondéré, volatilité, score & classement.
  const stats = (await performanceFournisseurs()).find((p) => p.tiersId === cible.id);
  if (stats) {
    lignes.push('');
    lignes.push(`📊 **Historique analytique** (${stats.livraisons} livraison(s) triturées, ${fmt(stats.kg)} kg) : rendement moyen analysé ${fmt(stats.rdtMoyen, 2)} % · écart pondéré ${stats.ecartPondere >= 0 ? '+' : ''}${fmt(stats.ecartPondere, 2)} pts · volatilité ${fmt(stats.volatilite, 2)} pts · bonus/malus analytique cumulé ${stats.bonus >= 0 ? '+' : ''}${fmt(stats.bonus, 1)} kg · score ${fmt(stats.score, 1)} → **classement n° ${stats.classement}**.`);
    lignes.push(stats.volatilite > 2 ? '⚠️ Fournisseur irrégulier (volatilité élevée) : analyser chaque livraison au FOSS.' : '✔️ Fournisseur régulier (faible volatilité).');
  }
  lignes.push('');
  lignes.push(`Détail des bons : ${lots.map((r) => `${r.id} (${r.date}, ${fmt(decompte(r).poidsNet)} kg${Number(r.huileExtraite) > 0 ? `, ${fmt(r.huileExtraite, 1)} kg huile` : ''})`).join(' · ')}`);
  return lignes.join('\n');
}
